import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";
import { AddEditScreeningProps, CRFOption, ScreeningFormValues } from "../helper/studyscreening.types";
import { studyScreeningForm } from "../helper/studyscreening.constant";

const AddEditStudyScreening: React.FC<AddEditScreeningProps> = ({
  existingData,
  onSave,
  setSubmitHandler,
  setSubmitState,
}) => {
  const [CRFList, setCRFList] = useState<CRFOption[]>([]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ScreeningFormValues>({
    defaultValues: {
      studyscreeningname: "",
      crfid: null,
    },
  });


  const { AssignCRFSelectAllQuery } = useCRFService({
    modulename: "Study Screening",
    pageNumber: 1,
    pageSize: 1000,
  });

  useEffect(() => {
    const rawData = (AssignCRFSelectAllQuery.data as any)?.data;
    const rawAssigned = Array.isArray(rawData) && rawData.length > 0
      ? rawData[0]?.assigned
      : rawData?.assigned;
    const assignedList = Array.isArray(rawAssigned) ? rawAssigned : [];

    const assignedOptions: CRFOption[] = assignedList.map((item: any) => ({
      label: item.crfname || item.name || `CRF ${item.crfid || item.id}`,
      value: String(item.crfid || item.id),
    }));

    const existingList = (existingData as any)?.crflist || [];
    const existingOptions: CRFOption[] = Array.isArray(existingList)
      ? existingList.map((item: any) => ({
          label: item.crfname || item.crfdetails?.title || `CRF ${item.crfid}`,
          value: String(item.crfid),
        }))
      : [];

    const mergedMap = new Map<string, CRFOption>();
    [...existingOptions, ...assignedOptions].forEach((opt) => {
      if (opt.value && !mergedMap.has(opt.value)) {
        mergedMap.set(opt.value, opt);
      }
    });

    const combinedList = Array.from(mergedMap.values());
    setCRFList(combinedList);

    // If adding a new record (not editing), select ALL assigned CRFs by default
    if (!existingData || !existingData.id) {
      const allIds = assignedOptions.map((opt) => opt.value);
      reset({
        studyscreeningname: existingData?.studyscreeningname ?? "",
        crfid: allIds,
      });
    }
  }, [AssignCRFSelectAllQuery.data, existingData]);

  // reset on edit
  useEffect(() => {
    if (existingData?.id) {
      reset({
        studyscreeningname: existingData?.studyscreeningname ?? "",
        crfid: existingData?.crfid,
      });
    }
  }, [existingData?.id]);

  // expose submit handler to parent
  useEffect(() => {
    if (setSubmitHandler) {
      if (!existingData?.id && CRFList.length === 0) {
        setSubmitHandler(null);
      } else {
        setSubmitHandler(() => handleSubmit(onSubmit));
      }
    }
    if (setSubmitState) {
      setSubmitState(isSubmitting);
    }
  }, [handleSubmit, isSubmitting, setSubmitHandler, setSubmitState, CRFList.length, existingData?.id]);

  /* =========================
     Submit
  ========================= */

  const onSubmit: SubmitHandler<ScreeningFormValues> = (values) => {
    const record = existingData ? { ...existingData, ...values } : values;
    console.log("record : ", record);

    onSave(record);
  };

  /* =========================
     Render
  ========================= */

  // Only show empty state if adding new screening and no CRFs exist
  if (!existingData?.id && CRFList.length === 0) {
    return (
      <div className="text-center py-4 px-3">
        <div
          className="mx-auto mb-3 text-warning bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: "50px", height: "50px" }}
        >
          <i className="ri-file-warning-line fs-24" />
        </div>
        <h6 className="fw-semibold text-dark mb-1">No CRFs Available</h6>
        <p className="text-muted fs-13 mb-0">
          No CRFs are currently assigned to the Study Screening module. Please assign CRFs under CRF Assignment before creating a screening.
        </p>
      </div>
    );
  }

  return (
    <form>
      <div className="crf-select-disabled-wrapper">
        <style>{`
          .crf-select-disabled-wrapper .common-select__control {
            background-color: #f8f9fa !important;
            border-color: #e2e8f0 !important;
            min-height: 31px !important;
            max-height: 90px !important;
            overflow-y: auto !important;
            pointer-events: auto !important;
            border-radius: 6px !important;
          }
          .crf-select-disabled-wrapper .common-select__control * {
            pointer-events: auto !important;
          }
          .crf-select-disabled-wrapper .common-select__value-container {
            max-height: 75px !important;
            overflow-y: auto !important;
            pointer-events: auto !important;
            padding: 3px 6px !important;
            gap: 2px !important;
          }
          .crf-select-disabled-wrapper .common-select__control::-webkit-scrollbar,
          .crf-select-disabled-wrapper .common-select__value-container::-webkit-scrollbar {
            width: 5px;
          }
          .crf-select-disabled-wrapper .common-select__control::-webkit-scrollbar-thumb,
          .crf-select-disabled-wrapper .common-select__value-container::-webkit-scrollbar-thumb {
            background-color: #94a3b8;
            border-radius: 4px;
          }
          .crf-select-disabled-wrapper .common-select__multi-value {
            background-color: #eef2f6 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 4px !important;
            margin: 1px 2px !important;
          }
          .crf-select-disabled-wrapper .common-select__multi-value__label {
            font-size: 11px !important;
            color: #334155 !important;
            font-weight: 500 !important;
            padding: 1px 6px !important;
          }
          .crf-select-disabled-wrapper .common-select__multi-value__remove {
            display: none !important;
          }
        `}</style>
        <CommonForm
          fields={studyScreeningForm(CRFList, true)}
          control={control}
          errors={errors}
          columns={2}
        />
      </div>
    </form>
  );
};

export default AddEditStudyScreening;
